import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import { useNavigate } from 'react-router-dom';

const AboutUs = () => {
  const navigate = useNavigate();
  const [hoveredService, setHoveredService] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(null);

  const services = [
    {
      title: 'Workplace Safety',
      description: 'Comprehensive safety training programs',
      icon: 'Shield',
      color: 'emerald',
      gradient: 'from-emerald-400 to-green-500',
      stats: '500+ Programs',
      features: ['OSHA Compliance', 'Emergency Response', 'Safety Protocols']
    },
    {
      title: 'Harassment Prevention',
      description: 'Creating respectful work environments',
      icon: 'Users',
      color: 'green',
      gradient: 'from-green-400 to-emerald-500',
      stats: '100K+ Trained',
      features: ['Prevention Training', 'Policy Development', 'Investigation Support']
    },
    {
      title: 'Regulatory Compliance',
      description: 'Industry-specific compliance solutions',
      icon: 'FileText',
      color: 'lime',
      gradient: 'from-lime-400 to-green-500',
      stats: '25+ Industries',
      features: ['Industry Standards', 'Audit Support', 'Documentation']
    },
    {
      title: 'Risk Management',
      description: 'Identify and mitigate organizational risks',
      icon: 'AlertTriangle',
      color: 'teal',
      gradient: 'from-teal-400 to-emerald-500',
      stats: '1000+ Audits',
      features: ['Risk Assessment', 'Mitigation Plans', 'Monitoring']
    }
  ];

  const features = [
    {
      icon: 'Zap',
      title: 'Fast Implementation',
      description: 'Get started in days, not months',
      gradient: 'from-lime-400 to-green-500'
    },
    {
      icon: 'Shield',
      title: '100% Secure',
      description: 'Enterprise-grade security standards',
      gradient: 'from-emerald-400 to-teal-500'
    },
    {
      icon: 'Heart',
      title: 'Expert Support',
      description: '24/7 dedicated customer success team',
      gradient: 'from-green-400 to-emerald-500'
    },
    {
      icon: 'TrendingUp',
      title: 'Proven Results',
      description: 'Track record of compliance success',
      gradient: 'from-teal-400 to-green-500'
    }
  ];

  const stats = [
    { number: '99%', label: 'Client Satisfaction', icon: 'Star', gradient: 'from-green-400 to-emerald-500' },
    { number: '24/7', label: 'Support Available', icon: 'Clock', gradient: 'from-emerald-400 to-teal-500' },
    { number: '50+', label: 'Expert Trainers', icon: 'Users', gradient: 'from-lime-400 to-green-500' },
    { number: '15+', label: 'Countries Served', icon: 'Globe', gradient: 'from-teal-400 to-emerald-500' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      {/* Hero Section - New Green Design */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
          {/* Animated Geometric Pattern */}
          <div className="absolute inset-0 opacity-30">
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute border-2 border-emerald-200/50 rounded-lg"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: `${Math.random() * 100 + 50}px`,
                  height: `${Math.random() * 100 + 50}px`,
                }}
                animate={{
                  rotate: [0, 180, 360],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: Math.random() * 10 + 10,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            ))}
          </div>
          
          {/* Floating Elements */}
          <div className="absolute inset-0">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className={`absolute rounded-full ${
                  i % 3 === 0 ? 'bg-emerald-200/40' :
                  i % 3 === 1 ? 'bg-green-200/40' :
                  'bg-teal-200/40'
                }`}
                style={{
                  width: `${Math.random() * 100 + 50}px`,
                  height: `${Math.random() * 100 + 50}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, Math.random() * 100 - 50],
                  x: [0, Math.random() * 50 - 25],
                }}
                transition={{
                  duration: Math.random() * 8 + 8,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500/10 to-green-500/10 backdrop-blur-sm rounded-full border border-emerald-400/20 mb-8"
              >
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  className="w-2 h-2 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full mr-2"
                />
                <span className="text-sm font-semibold text-emerald-700">
                  Leading Compliance Solutions Since 2005
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight"
              >
                Building a{' '}
                <span className="block mt-2 bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 bg-clip-text text-transparent">
                  Safer Workplace
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed"
              >
                We're ComplianceTrained - your trusted partner in navigating the complex world 
                of regulatory compliance with confidence and clarity. Empowering organizations 
                with cutting-edge training and compliance solutions.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white px-8 py-3 rounded-xl shadow-lg shadow-emerald-500/25"
                    onClick={() => navigate('/webinars/live')}
                  >
                    <Icon name="PlayCircle" size={20} className="mr-2" />
                    Explore Training
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-3 rounded-xl"
                    onClick={() => navigate('/contact')}
                  >
                    <Icon name="MessageSquare" size={20} className="mr-2" />
                    Get Consultation
                  </Button>
                </motion.div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="relative"
            >
              <div className="relative bg-white rounded-3xl p-8 shadow-2xl shadow-emerald-500/10 border border-emerald-100">
                <div className="grid grid-cols-3 gap-6 mb-8">
                  {[
                    { number: '25+', label: 'Years Experience', color: 'from-emerald-400 to-green-500' },
                    { number: '1000+', label: 'Companies', color: 'from-green-400 to-emerald-500' },
                    { number: '100K+', label: 'Trained', color: 'from-teal-400 to-green-500' }
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ y: -5 }}
                      className="text-center p-4 rounded-xl bg-gradient-to-b from-gray-50 to-white"
                    >
                      <motion.div
                        className={`text-3xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2`}
                        animate={{
                          textShadow: [
                            '0 0 0px rgba(34, 197, 94, 0)',
                            '0 0 20px rgba(34, 197, 94, 0.3)',
                            '0 0 0px rgba(34, 197, 94, 0)'
                          ]
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        {stat.number}
                      </motion.div>
                      <div className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="space-y-4">
                  {[
                    { text: 'ISO 9001 Certified', icon: 'Award' },
                    { text: '24/7 Expert Support', icon: 'Headphones' },
                    { text: 'Global Compliance Standards', icon: 'Globe' }
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      className="flex items-center p-3 rounded-lg bg-emerald-50/50"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center mr-3">
                        <Icon name={item.icon} size={16} className="text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-700">{item.text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Updated */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-emerald-100 to-green-100 rounded-full -translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full translate-x-32 translate-y-32"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-emerald-500/10 to-green-500/10 text-emerald-700 rounded-full text-sm font-semibold mb-4">
              Our Advantages
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Organizations Choose Us
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive solutions designed to meet your compliance needs efficiently
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -15 }}
                onHoverStart={() => setHoveredFeature(index)}
                onHoverEnd={() => setHoveredFeature(null)}
                className="relative group"
              >
                <motion.div
                  animate={{
                    scale: hoveredFeature === index ? 1.02 : 1,
                    rotateY: hoveredFeature === index ? 5 : 0,
                    rotateX: hoveredFeature === index ? -5 : 0
                  }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-2xl p-8 shadow-lg shadow-gray-200/50 border border-gray-100 h-full"
                >
                  <motion.div
                    className={`w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg`}
                    animate={{
                      rotate: hoveredFeature === index ? [0, 10, -10, 0] : 0,
                      scale: hoveredFeature === index ? 1.1 : 1
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <Icon name={feature.icon} size={24} className="text-white" />
                  </motion.div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-center leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>

                <motion.div
                  animate={{
                    opacity: hoveredFeature === index ? 0.1 : 0,
                    scale: hoveredFeature === index ? 1.1 : 1
                  }}
                  className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} rounded-2xl blur-xl -z-10`}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services - Updated */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-emerald-500/10 to-green-500/10 text-emerald-700 rounded-full text-sm font-semibold mb-4">
              Core Services
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Compliance Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              End-to-end services tailored to your industry requirements
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
                onHoverStart={() => setHoveredService(index)}
                onHoverEnd={() => setHoveredService(null)}
                className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-white to-${service.color}-50 border border-${service.color}-100`}
              >
                <div className="p-8">
                  <div className="flex items-start justify-between mb-6">
                    <motion.div
                      className={`w-14 h-14 bg-gradient-to-r ${service.gradient} rounded-xl flex items-center justify-center shadow-lg`}
                      animate={{
                        rotate: hoveredService === index ? 360 : 0
                      }}
                      transition={{ duration: 0.6 }}
                    >
                      <Icon name={service.icon} size={24} className="text-white" />
                    </motion.div>
                    <motion.span
                      animate={{
                        scale: hoveredService === index ? 1.1 : 1
                      }}
                      className={`px-4 py-2 bg-${service.color}-100 text-${service.color}-800 rounded-full text-sm font-semibold`}
                    >
                      {service.stats}
                    </motion.span>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {service.description}
                  </p>

                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                      height: hoveredService === index ? 'auto' : 0,
                      opacity: hoveredService === index ? 1 : 0
                    }}
                    className="overflow-hidden"
                  >
                    <div className="pt-6 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                        Key Features
                      </h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, i) => (
                          <motion.li
                            key={i}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{
                              x: hoveredService === index ? 0 : -20,
                              opacity: hoveredService === index ? 1 : 0
                            }}
                            transition={{ delay: i * 0.1 }}
                            className="flex items-center text-gray-700"
                          >
                            <Icon name="CheckCircle" size={16} className={`text-${service.color}-500 mr-3`} />
                            {feature}
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                </div>

                <motion.div
                  animate={{
                    scale: hoveredService === index ? 1 : 0
                  }}
                  className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${service.gradient}`}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers - Updated */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-emerald-900 via-green-900 to-teal-900 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute bg-gradient-to-r from-emerald-500 to-green-500 rounded-full blur-3xl"
              style={{
                width: `${Math.random() * 300 + 100}px`,
                height: `${Math.random() * 300 + 100}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                x: [0, Math.random() * 200 - 100],
                y: [0, Math.random() * 200 - 100],
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Excellence in Numbers
            </h2>
            <p className="text-lg text-emerald-200 max-w-2xl mx-auto">
              Quantifying our impact and commitment to quality
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="relative group"
              >
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center">
                  <motion.div
                    className={`w-16 h-16 bg-gradient-to-r ${stat.gradient} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Icon name={stat.icon} size={24} className="text-white" />
                  </motion.div>
                  <div className={`text-4xl font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent mb-3`}>
                    {stat.number}
                  </div>
                  <div className="text-sm font-medium text-emerald-200">
                    {stat.label}
                  </div>
                </div>

                <motion.div
                  animate={{
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className={`absolute inset-0 bg-gradient-to-r ${stat.gradient} rounded-2xl blur-xl opacity-30 -z-10 group-hover:opacity-50`}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team - Updated */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative rounded-3xl overflow-hidden shadow-2xl shadow-emerald-500/10"
                >
                  <Image
                    src="https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                    alt="Compliance team meeting"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/50 via-emerald-900/20 to-transparent"></div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-2xl border border-emerald-100"
                >
                  <div className="flex items-center space-x-4">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                      className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl flex items-center justify-center"
                    >
                      <Icon name="Award" size={24} className="text-white" />
                    </motion.div>
                    <div>
                      <div className="font-bold text-gray-900">Industry Leader 2024</div>
                      <div className="text-sm text-gray-600">Compliance Excellence Award</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-emerald-500/10 to-green-500/10 text-emerald-700 rounded-full text-sm font-semibold mb-6">
                Our Leadership
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Guided by Industry Experts
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our team combines decades of regulatory expertise with innovative 
                training methodologies to deliver exceptional compliance solutions 
                that drive organizational success.
              </p>

              <div className="space-y-6">
                {[
                  { icon: 'Users', title: 'Expert Team', description: '50+ certified compliance professionals', gradient: 'from-emerald-500 to-green-500' },
                  { icon: 'Award', title: 'Industry Recognition', description: 'Multiple awards and certifications', gradient: 'from-green-500 to-teal-500' },
                  { icon: 'Globe', title: 'Global Reach', description: 'Operating in 15+ countries worldwide', gradient: 'from-teal-500 to-emerald-500' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ x: 10 }}
                    className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gradient-to-r hover:from-emerald-50 hover:to-green-50 transition-all duration-300 group"
                  >
                    <motion.div
                      className={`w-12 h-12 bg-gradient-to-r ${item.gradient} rounded-xl flex items-center justify-center shadow-lg`}
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <Icon name={item.icon} size={20} className="text-white" />
                    </motion.div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-emerald-700 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-8"
              >
                <Button
                  variant="outline"
                  className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  onClick={() => navigate('/team')}
                >
                  Meet Our Team
                  <Icon name="ArrowRight" size={20} className="ml-2" />
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Updated */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
              Start Your Compliance Journey
            </h2>
            <p className="text-lg sm:text-xl text-emerald-100 mb-10 max-w-2xl mx-auto">
              Join thousands of organizations that trust us for regulatory excellence 
              and workplace safety.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  size="lg"
                  className="bg-white text-emerald-900 hover:bg-gray-100 px-8 py-4 rounded-xl font-semibold shadow-2xl"
                  onClick={() => navigate('/webinars/live')}
                >
                  <Icon name="PlayCircle" size={20} className="mr-2" />
                  Start Training
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-xl font-semibold"
                  onClick={() => navigate('/contact')}
                >
                  <Icon name="Calendar" size={20} className="mr-2" />
                  Schedule Demo
                </Button>
              </motion.div>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-emerald-100">
              {[
                { icon: 'ShieldCheck', text: 'Industry Certified' },
                { icon: 'Clock', text: '24/7 Expert Support' },
                { icon: 'CheckCircle', text: 'Satisfaction Guaranteed' }
              ].map((badge, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center space-x-2"
                >
                  <Icon name={badge.icon} size={18} className="text-emerald-200" />
                  <span className="text-sm font-medium">{badge.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;